import React, { useState, useEffect } from "react";

/* ---------- validators ---------- */
const isValidEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const isValidPhone = (phone) =>
    /^[6-9]\d{9}$/.test(phone); // Indian mobile numbers

/* ---------- component ---------- */
const ShippingAddressForm = ({ address, setAddress, onValidityChange }) => {
    const [errors, setErrors] = useState({});

    const isFormValid = () => {
        return (
            address.first_name?.trim() &&
            address.address?.trim() &&
            address.phone?.trim() &&
            address.email?.trim() &&
            address.city?.trim() &&
            address.zip?.trim() &&
            isValidEmail(address.email) &&
            isValidPhone(address.phone)
        );
    };

    useEffect(() => {
        if (onValidityChange) {
            onValidityChange(isFormValid());
        }
    }, [address]);


    /* ---------- handle input ---------- */
    const handleChange = (e) => {
        setAddress({ ...address, [e.target.name]: e.target.value });
    };

    /* ---------- pincode lookup ---------- */
    const handlePincodeLookup = async (pincode) => {
        if (pincode.length !== 6) return;

        try {
            const res = await fetch(
                `https://api.postalpincode.in/pincode/${pincode}`
            );
            const data = await res.json();

            if (data[0].Status === "Success") {
                const postOffice = data[0].PostOffice[0];

                setAddress((prev) => ({
                    ...prev,
                    city: postOffice.District,
                    province: postOffice.State,
                    country_code: "IN",
                }));
            }
        } catch (err) {
            console.error("Pincode lookup failed", err);
        }
    };

    /* ---------- phone normalization ---------- */
    const normalizePhone = (value) => {
        if (!value) return "";

        // Remove spaces, hyphens, brackets
        let phone = value.replace(/[^\d]/g, "");

        // Remove leading 0
        if (phone.startsWith("0")) {
            phone = phone.slice(1);
        }

        // Remove country code +91
        if (phone.startsWith("91") && phone.length > 10) {
            phone = phone.slice(2);
        }

        return phone;
    };


    /* ---------- validation ---------- */
    const validateField = (name, value) => {
        let message = "";

        if (
            ["first_name", "address", "phone", "email", "city", "zip"].includes(name)
            && !value
        ) {
            message = "This field is required";
        }

        if (name === "email" && value && !isValidEmail(value)) {
            message = "Invalid email address";
        }

        if (name === "phone" && value && !isValidPhone(value)) {
            message = "Invalid phone number";
        }

        setErrors((prev) => ({ ...prev, [name]: message }));
    };

    return (
        <form className="space-y-4">

            {/* First Name ⭐ */}
            <Input
                label="First Name"
                name="first_name"
                required
                value={address.first_name}
                onChange={handleChange}
                onBlur={validateField}
                error={errors.first_name}
            />

            {/* Last Name */}
            <Input
                label="Last Name"
                name="last_name"
                value={address.last_name}
                onChange={handleChange}
            />

            {/* Address ⭐ */}
            <Input
                label="Address"
                name="address"
                required
                value={address.address}
                onChange={handleChange}
                onBlur={validateField}
                error={errors.address}
            />

            {/* Phone ⭐ */}
            <Input
                label="Phone"
                name="phone"
                required
                value={address.phone}
                onChange={(e) => {
                    const cleaned = normalizePhone(e.target.value);
                    setAddress({ ...address, phone: cleaned });
                }}
                onBlur={validateField}
                error={errors.phone}
            />


            {/* Email ⭐ */}
            <Input
                label="Email"
                name="email"
                required
                value={address.email}
                onChange={(e) =>
                    handleChange({
                        target: {
                            name: "email",
                            value: e.target.value.trim(),
                        },
                    })
                }
                onBlur={validateField}
                error={errors.email}
            />


            {/* ZIP / PINCODE ⭐ */}
            <Input
                label="Pincode"
                name="zip"
                required
                value={address.zip}
                onChange={(e) => {
                    handleChange(e);
                    handlePincodeLookup(e.target.value);
                }}
                onBlur={validateField}
                error={errors.zip}
            />

            {/* City ⭐ */}
            <Input
                label="City"
                name="city"
                required
                value={address.city}
                onChange={handleChange}
                onBlur={validateField}
                error={errors.city}
            />

            {/* Province (State) */}
            <Input
                label="State"
                name="province"
                value={address.province}
                onChange={handleChange}
            />

            {/* Country (Auto) */}
            <Input
                label="Country"
                name="country_code"
                value="IN"
                disabled
            />
        </form>
    );
};

/* ---------- Reusable Input ---------- */
const Input = ({
    label,
    name,
    value,
    onChange,
    onBlur,
    required,
    error,
    disabled,
}) => (
    <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
            {label} {required && <span className="text-red-500">*</span>}
        </label>

        <input
            type="text"
            name={name}
            value={value || ""}
            onChange={onChange}
            onBlur={(e) => onBlur && onBlur(name, e.target.value)}
            disabled={disabled}
            className={`
        w-full px-4 py-3 rounded-xl
        bg-white/60 backdrop-blur-md
        border ${error ? "border-red-400" : "border-white/40"}
        text-gray-800
        focus:ring-2 focus:ring-blue-400
      `}
        />

        {error && (
            <p className="text-sm text-red-500 mt-1">{error}</p>
        )}
    </div>
);

export default ShippingAddressForm;
